import '../Styles.css'

import React, { useEffect, useState } from 'react'
import { QueryRequestProvider, QueryResponseProvider } from './core'
import { useQueryRequest } from './core/QueryRequestProvider'
import { useQueryResponseData } from './core/QueryResponseProvider' 
import { getAllDataForExport, getAgent, getCampaign } from './core/_requests'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import { DataTable } from './table/Table'
import { ListHeader } from './components/header/ListHeader'
import { useAuth } from '../../../../../../app/modules/auth'

interface ListProps {
  onStatsUpdate?: (stats: any) => void;
  dateRange?: {
    startDate: Date | null;
    endDate: Date | null;
  };
}

const List: React.FC<ListProps> = ({ onStatsUpdate, dateRange }) => {
  const { state, updateState } = useQueryRequest()
  const { currentUser } = useAuth() 
  const tableData = useQueryResponseData()
  
  const [agents, setAgents] = useState<Array<{ extension: number; nama: string }>>([])
  const [campaigns, setCampaigns] = useState<Array<{ name: string }>>([])
  const [isLoadingStats, setIsLoadingStats] = useState(true)

  const currentUserName = currentUser?.first_name || ''
  const roles = currentUser?.roles || []
  const isAgentOnly = roles.includes('ROLE_AGENT')

  const getStorageKey = () => {
    const userKey = currentUserName ? currentUserName.toLowerCase().replace(/\s+/g, '_') : 'default';
    return `recording_outbound_filter_${userKey}`;
  }

  const formatDuration = (value: any) => {
    if (!value) return "00:00:00";

    let totalSeconds = 0;

    if (typeof value === 'string' && (value.includes('-') || value.includes('T'))) {
      const startTime = new Date(value).getTime();
      const now = Date.now();
      totalSeconds = Math.floor((now - startTime) / 1000);
    } 
    else {
      totalSeconds = Math.floor(Number(value) || 0);
    }

    if (totalSeconds < 0 || totalSeconds > 2000000) return "00:00:00"; 

    const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
    const s = String(totalSeconds % 60).padStart(2, "0");

    return `${h}:${m}:${s}`;
  };

  const format = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [agentRes, campaignRes] = await Promise.all([getAgent(), getCampaign()])
        setAgents(agentRes.data)
        setCampaigns(campaignRes.data || campaignRes)
      } catch {
        toast.error('Failed to fetch initial data')
      } finally {
        setIsLoadingStats(false)
      }
    }
    fetchInitialData()
  }, [])

  const getFilterObject = (): Record<string, any> => {
    return typeof state.filter === 'object' && state.filter !== null
      ? (state.filter as Record<string, any>)
      : {}
  }

  const updateFilter = (key: string, value: any) => {
    if (key === 'agent' && isAgentOnly) return

    updateState({
      filter: {
        ...getFilterObject(),
        [key]: value,
      },
      page: 1,
    })
  }

  useEffect(() => {
    const currentFilter = getFilterObject();
    const today = new Date();
    const todayStr = format(today);
    
    let savedStart = '';
    let savedEnd = '';
    let savedAgent = '';
    let savedCampaign = '';
    let savedStatus = '';
    let hasSavedData = false;

    try {
      const saved = localStorage.getItem(getStorageKey());
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.startDate && parsed.endDate) {
          hasSavedData = true;
          savedStart = parsed.startDate;
          savedEnd = parsed.endDate;
          savedAgent = parsed.agent || '';
          savedCampaign = parsed.campaign || '';
          savedStatus = parsed.status || '';
        }
      }
    } catch {}
    
    let targetStart = dateRange?.startDate ? format(dateRange.startDate) : (hasSavedData ? savedStart : todayStr);
    let targetEnd = dateRange?.endDate ? format(dateRange.endDate) : (hasSavedData ? savedEnd : todayStr);
    
    const targetAgent = isAgentOnly 
      ? currentUserName 
      : (hasSavedData ? savedAgent : (currentFilter.agent || ''));
      
    const targetCampaign = hasSavedData ? savedCampaign : (currentFilter.campaign || '');
    const targetStatus = hasSavedData ? savedStatus : (currentFilter.status || '');
    
    if (currentFilter.startDate !== targetStart || 
        currentFilter.endDate !== targetEnd || 
        currentFilter.agent !== targetAgent ||
        currentFilter.campaign !== targetCampaign ||
        currentFilter.status !== targetStatus) {
      updateState({
        filter: {
          startDate: targetStart,
          endDate: targetEnd,
          agent: targetAgent || undefined,
          campaign: targetCampaign || undefined,
          status: targetStatus || undefined,
        },
        page: 1,
      });
    }
  }, [dateRange, currentUser, isAgentOnly, currentUserName]);

  useEffect(() => {
    try {
      const filter = getFilterObject();
      if (filter.startDate && filter.endDate) {
        localStorage.setItem(getStorageKey(), JSON.stringify({
          startDate: filter.startDate || null,
          endDate: filter.endDate || null,
          agent: filter.agent || null,
          campaign: filter.campaign || null,
          status: filter.status || null,
        }));
      }
    } catch {}
  }, [state.filter, currentUserName, isAgentOnly]);

  useEffect(() => {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
      if (onStatsUpdate) {
        onStatsUpdate({
          outbound: 0,
          outboundAnswered: 0,
          outboundNoAnswer: 0,
          outboundBusy: 0,
          outboundFailed: 0,
          outboundDuration: '00:00:00'
        });
      }
      return;
    }

    let total = 0, answered = 0, noAnswer = 0, busy = 0, failed = 0, totalDuration = 0;

    tableData.forEach((item: any) => {
      total++;
      const status = (item.status || '').toUpperCase().trim();
      const duration = parseInt(item.billsec) || 0;
      
      if (status === 'ANSWERED' || status === 'ANSWER') {
        answered++;
        totalDuration += duration;
      } else if (status === 'NO ANSWER' || status === 'NOANSWER' || status === 'NO_ANSWER') {
        noAnswer++;
      } else if (status === 'BUSY') {
        busy++;
      } else {
        failed++;
      }
    });

    const hrs = String(Math.floor(totalDuration / 3600)).padStart(2, '0');
    const mins = String(Math.floor((totalDuration % 3600) / 60)).padStart(2, '0');
    const secs = String(totalDuration % 60).padStart(2, '0');

    if (onStatsUpdate) {
      onStatsUpdate({
        outbound: total,
        outboundAnswered: answered,
        outboundNoAnswer: noAnswer,
        outboundBusy: busy,
        outboundFailed: failed,
        outboundDuration: `${hrs}:${mins}:${secs}`
      });
    }
  }, [tableData, onStatsUpdate]);

  const handleDateChange = ([start, end]: [Date | null, Date | null]) => {
    updateState({
      filter: {
        ...getFilterObject(),
        startDate: start ? format(start) : undefined,
        endDate: end ? format(end) : undefined,
      },
      page: 1,
    })
  }

  const handleClear = () => {
    const today = format(new Date());
    localStorage.removeItem(getStorageKey());
    updateState({
      search: '',
      filter: {
        startDate: today,
        endDate: today,
        agent: isAgentOnly ? currentUserName : undefined,
      },
      sort: undefined,
      order: undefined,
      page: 1,
    })
  }

  const handleExport = async () => {
    const toastId = toast.loading('Preparing data for export...');
    try {
      const filter = getFilterObject()
      
      if (isAgentOnly) {
        filter.agent = currentUserName
      }

      const params = new URLSearchParams()
      const filterEntries = Object.entries(filter || {})

      filterEntries.forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          params.append(`filter_${key.toLowerCase()}`, value.toString().toLowerCase())
        }
      })

      if (state.search) {
        params.append('search', state.search)
      }

      const queryString = params.toString().replace(/\+/g, '%20')
      
      const res = await getAllDataForExport(queryString)
      const rawData = (res.data.data as any)?.items || res.data.data || [];
      
      if (!Array.isArray(rawData) || rawData.length === 0) {
        toast.dismiss(toastId)
        toast.warn('Tidak ada data untuk di-export')
        return
      }

      const exportData = rawData.map((item: any) => {
        const notesObj = item.Notes || item.notes;
        let formattedNotes = '-';

        if (notesObj && typeof notesObj === 'object') {
          formattedNotes = Object.entries(notesObj)
            .map(([key, valueObj]: [string, any]) => {
              const val = valueObj?.value !== undefined && valueObj?.value !== null ? valueObj.value : '-';
              return `• ${key}: ${val}`;
            })
            .join('\n');
        } else if (typeof notesObj === 'string' && notesObj) {
          formattedNotes = notesObj;
        }

        let reasonText = '-';
        if (item.reason || item.reasonBy) {
          const reason = item.reason || '';
          const reasonBy = item.reasonBy || '';
          reasonText = reason && reasonBy ? `${reason}-${reasonBy}` : `${reason}${reasonBy}`;
        }

        return {
          'Agent': item.agent || item.agentName || '-',
          'Extension': item.extension || '-',
          'Call Date': item.callDate || '-',
          'Phone Number': item.phoneNumber || '-',
          'Status': item.status || '-',
          'Reason': reasonText,
          'Duration': formatDuration(item.billsec) || 0,
          'Waiting Time': formatDuration(item.waitingTime) || 0,
          'Notes': formattedNotes,
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Recording Outbound List')

      XLSX.writeFile(wb, `Outbound_Recording_${new Date().toISOString().split('T')[0]}.xlsx`)
      toast.update(toastId, {
        render: 'Export successful!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      })
    } catch (error) {
      console.error('Export Error:', error)
      toast.update(toastId, {
        render: 'Gagal mengekspor data.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  const getDateRangeFromState = () => {
    const filter = getFilterObject()
    return {
      startDate: filter.startDate ? new Date(filter.startDate) : null,
      endDate: filter.endDate ? new Date(filter.endDate) : null,
    }
  }

  return (
    <div className='report-call-page-wrapper'>
      <ListHeader
        dateRange={getDateRangeFromState()}
        onDateChange={handleDateChange}
        agent={isAgentOnly ? currentUserName : (getFilterObject().agent || '')}
        campaign={getFilterObject().campaign || ''}
        status={getFilterObject().status || ''}
        onAgentChange={(v) => updateFilter('agent', v)}
        onCampaignChange={(v) => updateFilter('campaign', v)}
        onStatusChange={(v) => updateFilter('status', v)}
        search={state.search || ''}
        onSearchChange={(v) => updateState({ search: v, page: 1 })}
        onFilter={() => updateState({ page: 1 })}
        onClear={handleClear}
        onExport={handleExport}
        agents={agents}
        campaigns={campaigns}
        itemsPerPage={state.items_per_page}
        onItemsPerPageChange={(v) =>
          updateState({ items_per_page: v as 10 | 25 | 50 | 100, page: 1 })
        }
      />
      <div className='glass-card'>
        <DataTable />
      </div>
    </div>
  )
}
const ListWrapper: React.FC<ListProps> = (props) => (
  <QueryRequestProvider>
    <QueryResponseProvider>
      <List {...props} />
    </QueryResponseProvider>
  </QueryRequestProvider>
)

export { ListWrapper }
